import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useEffect } from 'react';

export default function MarketsPage() {
  useEffect(() => {
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Our Markets - Exalt Ventures Ltd.",
      "description": "Serving customers across North Africa and Malta with renowned fashion brands and exceptional retail experiences.",
      "url": `${import.meta.env.VITE_SITE_URL || "https://exaltventures.com"}/markets`,
      "about": {
        "@type": "Organization",
        "name": "Exalt Ventures Ltd.",
        "location": [
          {
            "@type": "Place",
            "name": "Libya",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Tripoli",
              "addressCountry": "LY"
            }
          },
          {
            "@type": "Place",
            "name": "Algeria",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Algiers",
              "addressCountry": "DZ"
            }
          },
          {
            "@type": "Place",
            "name": "Malta",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Sliema",
              "addressCountry": "MT"
            }
          }
        ]
      }
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-serif text-[#003264] mb-6 leading-tight">Our Markets</h1>
            <p className="text-base text-[#003264] leading-relaxed">
              Serving customers across North Africa and Malta with renowned fashion brands and exceptional retail experiences.
            </p>
          </div>
        </div>
      </section>

      {/* Libya */}
      <section className="py-16 bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white overflow-hidden">
              <div className="w-full h-72">
                <img 
                  src="https://static.readdy.ai/image/e7596bc6af9def0d5965eecdb80c30c5/d7306212353922581aea049fbcdb8ee9.png" 
                  alt="Libya Market"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-12">
                <h2 className="text-3xl font-serif text-[#003264] mb-6">Libya</h2>
                <p className="text-sm text-[#003264] mb-10 leading-relaxed">
                  Our longest-standing market, Libya represents the foundation of Exalt Ventures' regional presence. Since 2008, we have been serving Libyan customers with renowned British fashion brands through strategically located retail stores.
                </p>
                
                <div className="grid md:grid-cols-2 gap-10">
                  <div>
                    <h3 className="text-xl font-serif text-[#003264] mb-5">Market Overview</h3>
                    <div className="space-y-3 text-sm text-[#003264]">
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Established retail operations since 2008</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Multiple store locations in prime shopping areas</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Strong brand recognition and customer loyalty</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Growing demand for international fashion brands</span>
                      </p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif text-[#003264] mb-5">Brand Presence</h3>
                    <div className="space-y-3 text-sm text-[#003264]">
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>3 Accessorize stores offering women's accessories</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>3 Monsoon Children stores for kids' fashion</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Dune London footwear and accessories</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Comprehensive product range across all categories</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Algeria */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-[#FAFAFA] overflow-hidden">
              <div className="w-full h-72">
                <img 
                  src="https://static.readdy.ai/image/e7596bc6af9def0d5965eecdb80c30c5/aa668ca3cd4f6e4d3f4b28d3e6002d06.png" 
                  alt="Algeria Market"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-12">
                <h2 className="text-3xl font-serif text-[#003264] mb-6">Algeria</h2>
                <p className="text-sm text-[#003264] mb-10 leading-relaxed">
                  Algeria represents a significant growth market for Exalt Ventures. Since entering in 2017, we have successfully established a strong retail presence, bringing premium British fashion to Algerian customers who appreciate quality and style.
                </p>
                
                <div className="grid md:grid-cols-2 gap-10">
                  <div>
                    <h3 className="text-xl font-serif text-[#003264] mb-5">Market Overview</h3>
                    <div className="space-y-3 text-sm text-[#003264]">
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Market entry in 2017 with strategic expansion</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Rapidly growing fashion retail sector</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Increasing consumer sophistication and purchasing power</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Strong appetite for international brands</span>
                      </p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif text-[#003264] mb-5">Brand Presence</h3>
                    <div className="space-y-3 text-sm text-[#003264]">
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>3 Accessorize stores in premium locations</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>3 Monsoon Children stores serving families</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Dune London premium footwear collection</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Continued expansion plans for additional stores</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Malta */}
      <section className="py-16 bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white overflow-hidden">
              <div className="w-full h-72">
                <img 
                  src="https://static.readdy.ai/image/e7596bc6af9def0d5965eecdb80c30c5/3b17583affa63cd80ab0324a26e02578.png" 
                  alt="Malta Market"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-12">
                <h2 className="text-3xl font-serif text-[#003264] mb-6">Malta</h2>
                <p className="text-sm text-[#003264] mb-10 leading-relaxed">
                  Malta serves as our European gateway, offering access to both local and international customers. Operating in Malta since 2019, our Dune London stores have become destinations for fashion-conscious shoppers seeking the affordable luxury footwear and accessories.
                </p>
                
                <div className="grid md:grid-cols-2 gap-10">
                  <div>
                    <h3 className="text-xl font-serif text-[#003264] mb-5">Market Overview</h3>
                    <div className="space-y-3 text-sm text-[#003264]">
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>European market presence since 2019</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Strategic location serving local and tourist markets</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>High-quality retail environment with international standards</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Year-round shopping destination</span>
                      </p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-serif text-[#003264] mb-5">Brand Presence</h3>
                    <div className="space-y-3 text-sm text-[#003264]">
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>4 Dune London retail stores</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Premium locations in major shopping districts</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Complete range of footwear and accessories</span>
                      </p>
                      <p className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#64C8FA] rounded-full mr-2 mt-1.5"></span>
                        <span>Exceptional customer service and shopping experience</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
